import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Select,
  TimePicker,
  message,
} from 'antd';
import moment from 'moment';

import CompanySettingsCard from '../CompanySettingsCard';
import CompanyEditRow from '../CompanyEditRow';
import OnTraccrSwitch from '../../common/inputs/OnTraccrSwitch';
import { saveCompanySettings } from '../state/settings.actions';

const DATE_OPTIONS = [
  { label: 'Current Day', value: 'currentDay' },
  { label: 'Next Day', value: 'nextDay' },
];

const HELP_TEXT = 'Applies to shifts created on the Field Schedule and through the Shift Table.';
const SKIP_WEEKENDS_HELP_TEXT = 'When the next day falls on a Saturday or Sunday, default to the following Monday.';

const EMPTY_DEFAULT_SHIFT = {
  date: null,
  skipWeekends: false,
  startTime: null,
  endTime: null,
};

const parseTime = (value) => (value ? moment(`2000-01-01 ${value}`, 'YYYY-MM-DD HH:mm') : null);

export default function ScheduleDefaultDateTime() {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settings.company.settings) ?? {};
  const defaultShift = settings.defaultShift ?? EMPTY_DEFAULT_SHIFT;
  const {
    date = null,
    skipWeekends = false,
    startTime = null,
    endTime = null,
  } = defaultShift;

  const startMoment = parseTime(startTime);
  const endMoment = parseTime(endTime);
  const timeRangeValue = startMoment && endMoment ? [startMoment, endMoment] : null;

  const saveDefaultShift = (updates) => {
    dispatch(saveCompanySettings({
      defaultShift: {
        ...defaultShift,
        ...updates,
      },
    }));
  };

  const onDateChange = (value) => {
    saveDefaultShift({ date: value ?? null });
  };

  const onSkipWeekendsChange = (checked) => {
    saveDefaultShift({ skipWeekends: checked });
  };

  const onTimeRangeChange = (newTimes) => {
    const [startMoment, endMoment] = newTimes || [];
    if (!moment.isMoment(startMoment) || !moment.isMoment(endMoment)) {
      saveDefaultShift({
        startTime: null,
        endTime: null,
      });
      return;
    }

    if (startMoment.isAfter(endMoment)) {
      message.error('Start time must be before end time');
      return;
    }

    saveDefaultShift({
      startTime: startMoment.format('HH:mm'),
      endTime: endMoment.format('HH:mm'),
    });
  };

  return (
    <Row justify="start">
      <CompanySettingsCard title="Default Shift Date & Time">
        <CompanyEditRow
          title="Default Date"
          helpText={HELP_TEXT}
        >
          <Select
            allowClear
            placeholder="Select date"
            value={date}
            onChange={onDateChange}
            options={DATE_OPTIONS}
            style={{ width: 160 }}
          />
        </CompanyEditRow>
        <CompanyEditRow
          title="Skip Weekends"
          helpText={SKIP_WEEKENDS_HELP_TEXT}
        >
          <OnTraccrSwitch
            value={skipWeekends}
            onChange={onSkipWeekendsChange}
            disabled={date !== 'nextDay'}
          />
        </CompanyEditRow>
        <CompanyEditRow title="Default Time" divider={false}>
          <TimePicker.RangePicker
            style={{ width: 285 }}
            className="form-field-datetime-container"
            format="hh:mm a"
            minuteStep={15}
            use12Hours
            allowClear
            placeholder={['Start', 'End']}
            onChange={onTimeRangeChange}
            value={timeRangeValue}
          />
        </CompanyEditRow>
      </CompanySettingsCard>
    </Row>
  );
}
